import { Injectable } from "@angular/core";
import { ConnectionType } from "@common/ADAPT.Common.Model/organisation/connection-type";
import { Role, RoleBreezeModel } from "@common/ADAPT.Common.Model/organisation/role";
import { RoleLocation, RoleLocationBreezeModel } from "@common/ADAPT.Common.Model/organisation/role-location";
import { RoleType, RoleTypeBreezeModel } from "@common/ADAPT.Common.Model/organisation/role-type";
import { RoleTypeCode } from "@common/ADAPT.Common.Model/organisation/role-type-code";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { MethodologyPredicate } from "@common/lib/data/methodology-predicate";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import moment from "moment";
import { map, switchMap } from "rxjs/operators";
import { OrganisationService } from "../organisation/organisation.service";

@Injectable({
    providedIn: "root",
})
export class CommonIntegratedArchitectureFrameworkService {
    public constructor(
        private commonDataService: CommonDataService,
        private organisationService: OrganisationService,
    ) {
    }

    public getAllRoleTypes() {
        return this.commonDataService.getAll(RoleTypeBreezeModel);
    }

    public getRoleTypeByCode(code: string) {
        const predicate = new MethodologyPredicate<RoleType>("code", "==", code);

        return this.commonDataService.getByPredicate(RoleTypeBreezeModel, predicate).pipe(
            map(ArrayUtilities.getSingleFromArray),
        );
    }

    public getRoleTypesByCodes(codes: RoleTypeCode[]) {
        const predicate = new MethodologyPredicate<RoleType>("code", "in", codes);

        return this.commonDataService.getByPredicate(RoleTypeBreezeModel, predicate);
    }

    public getRoleLocationsForTeam(teamId: number) {
        const predicate = new MethodologyPredicate<RoleLocation>("teamId", "==", teamId);
        return this.commonDataService.getByPredicate(RoleLocationBreezeModel, predicate);
    }

    public getRoleLocationsForTeamIds(teamIds: number[]) {
        const predicate = new MethodologyPredicate<RoleLocation>("teamId", "in", teamIds);
        return this.commonDataService.getByPredicate(RoleLocationBreezeModel, predicate);
    }

    public getTeamRoleLocationsForRole(roleId: number) {
        const predicate = new MethodologyPredicate<RoleLocation>("roleId", "==", roleId)
            .and(new MethodologyPredicate<RoleLocation>("teamId", "!=", null));
        return this.commonDataService.getByPredicate(RoleLocationBreezeModel, predicate);
    }

    /**
     * Promise to create a new role for the existing organisation.
     * @param values The optional values to populate the role with
     * @returns A promise that resolves with the new role.
     */
    public createRole(values: Partial<Role> = {}) {
        return this.getAllRoleTypes().pipe(
            switchMap((roleTypes) => {
                const defaults: Partial<Role> = Object.assign({}, values, {
                    organisationId: this.organisationService.getOrganisationId(),
                    startDate: moment().toDate(),
                });

                if (!defaults.connectionType) {
                    defaults.connectionType = ConnectionType.Employee;
                }

                // default to Other roleType if none provided
                if (!defaults.roleType && !defaults.roleTypeId) {
                    defaults.roleType = roleTypes.find((rt) => rt.code === RoleTypeCode.Other);
                }

                return this.commonDataService.create(RoleBreezeModel, defaults);
            }),
        );
    }

    public createRoleLocationForTeam(role: Role, team: Team) {
        return this.getRoleLocationsForTeam(team.teamId).pipe(
            switchMap((roleLocations) => {
                const lastOrdinal = Math.max(...roleLocations.map((rl) => rl.ordinal), -1);
                return this.commonDataService.create(RoleLocationBreezeModel, { role, team, ordinal: lastOrdinal + 1 });
            }),
        );
    }
}
